import { useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import MSMPagination from '../../MSMPagination';
import Button from "react-bootstrap/Button";
import { Search, XCircle } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import UserService from "../../../services/user.service";
import Form from "react-bootstrap/Form";
import { Container, Row, Col, Table, Badge, Alert } from "react-bootstrap";

const SuperAdminUsers = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState([1,10]);
  const [resetPagination, setResetPagination] = useState(false);
  const [searchTerm, setSearchTerm] = useState(" -- ");
  const [searchInput, setSearchInput] = useState("");
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  let perPage = 10;

  useEffect(() => {
    loadUsers();
  }, [range, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await UserService.searchAllUsers(range, searchTerm);
      setUsers(response.data || []);
      setUserTotal(response.count || 0);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const reloadData = (newRange) => {
    setRange(newRange);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setResetPagination(!resetPagination);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm(" -- ");
    setResetPagination(!resetPagination);
  };

  const handleUserClick = (userId) => {
    navigate(`/admin/super/user/${userId}`);
  };

  return (
    <Container style={{padding: 4}}>
      <Row>
        <Col>
          <h2>User Manager</h2>
          <p className="text-muted">Search for users across all subsites</p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form onSubmit={handleSearchSubmit}>
            <div className="d-flex">
              <Form.Control
                type="text"
                placeholder="Search by email, username, first name, or last name..."
                value={searchInput}
                onChange={handleSearchChange}
              />
              <Button
                variant="primary"
                type="submit"
                className="ms-2"
                style={{ whiteSpace: 'nowrap' }}
              >
                <Search /> Search
              </Button>
              {searchTerm && (
                <Button
                  variant="secondary"
                  onClick={handleClearSearch}
                  className="ms-2"
                  title="Clear search"
                >
                  <XCircle />
                </Button>
              )}
            </div>
          </Form>
        </Col>
      </Row>

      {searchTerm && (
        <Row className="mb-2">
          <Col>
            <Alert variant="info" className="mb-2">
              Searching for: <strong>{searchTerm}</strong>
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-2">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <MSMPagination reload={reloadData} perPage={perPage} total={userTotal} reset={resetPagination} />
        </Col>
      </Row>

      {loading ? (
        <Row>
          <Col className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Col>
        </Row>
      ) : users.length === 0 ? (
        <Row>
          <Col>
            <Alert variant="info">
              {searchTerm ? 'No users found matching your search.' : 'Enter a search term to find users.'}
            </Alert>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col>
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Subsites</th>
                  <th>Balances</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.firstname} {user.lastname}</td>
                    <td>{user.email}</td>
                    <td>{user.username}</td>
                    <td>
                      {user.user_subsites && user.user_subsites.length > 0 ? (
                        <div>
                          {user.user_subsites.map((us, idx) => (
                            <Badge key={idx} bg="secondary" className="me-1 mb-1">
                              {us.subsite?.subsiteName || 'Unknown'}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No subsites</span>
                      )}
                    </td>
                    <td>
                      {user.subsite_balances && user.subsite_balances.length > 0 ? (
                        <div>
                          {user.subsite_balances.map((balance, idx) => (
                            <div key={idx} className="mb-1">
                              <small className="text-muted">
                                {balance.subsite?.subsiteName || 'Unknown'}:
                              </small>{' '}
                              <strong>
                                {balance.balance != null
                                  ? `$${parseFloat(balance.balance).toFixed(2)}`
                                  : 'N/A'}
                              </strong>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No balances</span>
                      )}
                    </td>
                    <td>
                      {user.active ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="danger">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleUserClick(user.id)}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}
    </Container>
  );
};
export default SuperAdminUsers;

